import { useState, FormEvent } from 'react';
import { Link } from 'react-router';
import { useDarkMode } from '../context/DarkModeContext';
import { api } from '../lib/api';
import { ArrowLeft, Mail } from 'lucide-react';
import { Logo } from '../components/Logo';

export default function ForgotPassword() {
  const { darkMode } = useDarkMode();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('E-Mail ist erforderlich');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      await api.post('/api/auth/forgot-password', { email });
      setSent(true);
    } catch (err: any) {
      setError(err.message || 'Ein Fehler ist aufgetreten');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = `w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
    error
      ? 'border-red-500'
      : darkMode
        ? 'border-slate-600'
        : 'border-slate-300'
  } ${
    darkMode
      ? 'bg-slate-700 text-white placeholder-slate-400'
      : 'bg-white text-slate-900 placeholder-slate-400'
  }`;

  return (
    <div
      className={`min-h-screen flex items-center justify-center px-4 py-12 ${
        darkMode ? 'bg-slate-900' : 'bg-slate-50'
      }`}
    >
      <div
        className={`w-full max-w-md rounded-2xl border shadow-lg p-8 ${
          darkMode
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-slate-200'
        }`}
      >
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="mb-3">
            <Logo size="lg" />
          </div>
          <h1
            className={`text-2xl font-bold ${
              darkMode ? 'text-white' : 'text-slate-900'
            }`}
          >
            Passwort vergessen
          </h1>
          <p
            className={`mt-1 text-sm text-center ${
              darkMode ? 'text-slate-400' : 'text-slate-500'
            }`}
          >
            Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link
            zum Zurücksetzen
          </p>
        </div>

        {sent ? (
          /* Success State */
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h2
              className={`text-lg font-semibold mb-2 ${
                darkMode ? 'text-white' : 'text-slate-900'
              }`}
            >
              E-Mail gesendet
            </h2>
            <p
              className={`text-sm mb-6 ${
                darkMode ? 'text-slate-400' : 'text-slate-500'
              }`}
            >
              Falls ein Konto mit dieser E-Mail existiert, erhalten Sie in Kürze
              einen Link zum Zurücksetzen Ihres Passworts.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm cursor-pointer"
            >
              <ArrowLeft size={16} />
              Zurück zur Anmeldung
            </Link>
          </div>
        ) : (
          /* Form */
          <>
            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    darkMode ? 'text-slate-300' : 'text-slate-700'
                  }`}
                >
                  E-Mail
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  placeholder="max@beispiel.de"
                  autoComplete="email"
                  className={inputClass}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                <span>{isSubmitting ? 'Wird gesendet...' : 'Link senden'}</span>
              </button>
            </form>

            <p
              className={`mt-6 text-center text-sm ${
                darkMode ? 'text-slate-400' : 'text-slate-500'
              }`}
            >
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium cursor-pointer"
              >
                <ArrowLeft size={14} />
                Zurück zur Anmeldung
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
